<template>
  <el-dialog
    :title="title"
    :visible.sync="dialogVisible"
    width="520px"
    append-to-body
    class="user-picker-dialog"
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <div class="picker-body">
      <el-input
        v-model="keyword"
        class="picker-search"
        placeholder="搜索帐号名或用户名"
        prefix-icon="el-icon-search"
        clearable
        @input="onKeywordInput"
        @clear="onSearchClear"
      />

      <div v-if="draftSelectedList.length > 0" class="picker-selected">
        <div class="picker-selected__head">
          <span class="picker-selected__label">已选</span>
          <span class="picker-selected__count">{{ draftSelectedList.length }} 人</span>
        </div>
        <div class="picker-selected__tags">
          <span
            v-for="user in draftSelectedList"
            :key="user.userId"
            class="picker-tag"
          >
            <span class="picker-tag__text" :title="`${user.userName}（${user.account}）`">
              {{ user.userName }}
            </span>
            <i
              class="el-icon-close picker-tag__remove"
              role="button"
              aria-label="移除"
              tabindex="0"
              @click.stop="removeUser(user.userId)"
              @keydown.enter.prevent="removeUser(user.userId)"
            />
          </span>
        </div>
      </div>

      <div
        ref="listScroll"
        class="picker-list"
        v-loading="loading && userList.length === 0"
        @scroll="onListScroll"
      >
        <template v-if="userList.length > 0">
          <div
            v-for="user in userList"
            :key="user.userId"
            class="picker-list__item"
            :class="{ 'is-selected': isSelected(user.userId) }"
            role="button"
            tabindex="0"
            @click="toggleUser(user)"
            @keydown.enter.prevent="toggleUser(user)"
          >
            <span class="picker-list__indicator" :class="{ 'is-checked': isSelected(user.userId) }" aria-hidden="true">
              <i v-if="isSelected(user.userId)" class="el-icon-check" />
            </span>
            <div class="picker-list__info">
              <p class="picker-list__account">{{ user.account }}</p>
              <p class="picker-list__name">{{ user.userName }}</p>
            </div>
          </div>
          <div v-if="loadingMore" class="picker-list__footer">
            <i class="el-icon-loading" /> 加载中…
          </div>
          <div v-else-if="!hasMore" class="picker-list__footer picker-list__footer--muted">没有更多了</div>
        </template>
        <div v-else-if="!loading" class="picker-empty">
          <i class="el-icon-user" />
          <p>{{ emptyText }}</p>
        </div>
      </div>
    </div>

    <span slot="footer" class="picker-footer">
      <el-button type="text" class="picker-footer__cancel" @click="dialogVisible = false">取 消</el-button>
      <el-button type="primary" class="picker-footer__confirm" @click="handleConfirm">确 定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { debounce } from 'throttle-debounce'
import { getFormAssistantList } from '@/api/project/form'
import { listUser } from '@/api/mange/user'
import { filterEmptyParams } from '@/utils/formatObject'

const PAGE_SIZE = 20

export function pickUser(item) {
  return {
    formKey: item.formKey,
    userId: item.userId,
    userName: item.userName,
    account: item.account,
    isCurrentUser: item.isCurrentUser
  }
}

export default {
  name: 'UserPickerDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '选择用户'
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      keyword: '',
      userList: [],
      draftSelectedMap: {},
      page: 1,
      hasMore: true,
      loading: false,
      loadingMore: false,
      clientSideCache: null,
      serverPaged: null
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    draftSelectedList() {
      return Object.values(this.draftSelectedMap)
    },
    emptyText() {
      return this.keyword.trim() ? '未找到匹配用户，请换个关键词试试' : '暂无用户数据'
    }
  },
  created() {
    this.debouncedSearch = debounce(300, () => {
      this.fetchUsers(true)
    })
  },
  beforeDestroy() {
    this.debouncedSearch?.cancel?.()
  },
  methods: {
    handleOpen() {
      this.initDraftFromValue()
      this.keyword = ''
      this.clientSideCache = null
      this.serverPaged = null
      this.fetchUsers(true)
    },
    handleClosed() {
      this.userList = []
      this.keyword = ''
      this.page = 1
      this.hasMore = true
      this.clientSideCache = null
      this.serverPaged = null
    },
    initDraftFromValue() {
      const map = {}
      ;(this.value || []).forEach((item) => {
        const user = pickUser(item)
        map[user.userId] = user
      })
      this.draftSelectedMap = map
    },
    onKeywordInput() {
      this.debouncedSearch()
    },
    onSearchClear() {
      this.debouncedSearch()
    },
    onListScroll() {
      const el = this.$refs.listScroll
      if (!el || this.loading || this.loadingMore || !this.hasMore) return
      const threshold = 48
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
        this.fetchUsers(false)
      }
    },
    parseResponse(res) {
      const data = res?.data
      if (Array.isArray(data)) {
        return { list: data, total: data.length, isPaged: false }
      }
      if (data && Array.isArray(data.records)) {
        return {
          list: data.records,
          total: data.total ?? data.records.length,
          isPaged: true
        }
      }
      return { list: [], total: 0, isPaged: false }
    },
    filterByKeyword(list, text) {
      const q = (text || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(
        (item) =>
          (item.account && item.account.toLowerCase().includes(q)) ||
          (item.userName && item.userName.toLowerCase().includes(q))
      )
    },
    mapListUserRecord(row) {
      return pickUser({
        userId: row.id ?? row.userId,
        userName: row.name ?? row.userName,
        account: row.account,
        formKey: row.formKey,
        isCurrentUser: row.isCurrentUser
      })
    },
    async requestUserPage(currentPage, keyword) {
      const simpleParams = filterEmptyParams({
        text: keyword,
        current: currentPage,
        size: PAGE_SIZE
      })
      const simpleRes = await getFormAssistantList(simpleParams)
      const simpleParsed = this.parseResponse(simpleRes)
      if (simpleParsed.list.length > 0 || keyword) {
        return simpleParsed
      }
      const pageParams = filterEmptyParams({
        current: currentPage,
        size: PAGE_SIZE,
        userQueryEnum: 'name',
        userQueryStr: keyword
      })
      const pageRes = await listUser(pageParams)
      const records = pageRes?.data?.records || []
      return {
        list: records.map((row) => this.mapListUserRecord(row)),
        total: pageRes?.data?.total ?? records.length,
        isPaged: true
      }
    },
    async fetchUsers(reset) {
      if (reset) {
        this.page = 1
        this.userList = []
        this.hasMore = true
        this.clientSideCache = null
        this.serverPaged = null
      } else if (!this.hasMore) {
        return
      }

      const currentPage = this.page
      const isFirstPage = currentPage === 1
      if (isFirstPage) {
        this.loading = true
      } else {
        this.loadingMore = true
      }

      try {
        const keyword = this.keyword.trim()
        const { list, total, isPaged } = await this.requestUserPage(currentPage, keyword)
        const normalized = list.map((item) => pickUser(item))

        if (isPaged) {
          this.serverPaged = true
          this.userList = reset ? normalized : [...this.userList, ...normalized]
          this.hasMore = normalized.length > 0 && this.userList.length < total
        } else {
          this.serverPaged = false
          if (reset || !this.clientSideCache) {
            this.clientSideCache = normalized
          }
          const filtered = this.filterByKeyword(this.clientSideCache, keyword)
          const start = (currentPage - 1) * PAGE_SIZE
          const chunk = filtered.slice(start, start + PAGE_SIZE)
          this.userList = reset ? chunk : [...this.userList, ...chunk]
          this.hasMore = chunk.length > 0 && start + PAGE_SIZE < filtered.length
        }

        if (normalized.length === 0) {
          this.hasMore = false
        } else if (this.hasMore) {
          this.page = currentPage + 1
        }
      } catch {
        this.hasMore = false
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    isSelected(userId) {
      return !!this.draftSelectedMap[userId]
    },
    toggleUser(user) {
      if (this.isSelected(user.userId)) {
        this.removeUser(user.userId)
      } else {
        this.$set(this.draftSelectedMap, user.userId, pickUser(user))
      }
    },
    removeUser(userId) {
      this.$delete(this.draftSelectedMap, userId)
    },
    handleConfirm() {
      const selected = this.draftSelectedList.map((item) => pickUser(item))
      this.$emit('input', selected)
      this.$emit('confirm', selected)
      this.dialogVisible = false
    }
  }
}
</script>

<style lang="scss" scoped>
.picker-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.picker-search {
  ::v-deep .el-input__inner {
    border-radius: 6px;
    border-color: #e2e8f0;

    &:focus {
      border-color: #00a3ff;
    }
  }
}

.picker-selected {
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
  }

  &__count {
    font-size: 12px;
    color: #64748b;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 88px;
    overflow-y: auto;
  }
}

.picker-tag {
  display: inline-flex;
  align-items: center;
  max-width: 160px;
  padding: 4px 8px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 12px;
  color: #334155;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__remove {
    margin-left: 4px;
    padding: 2px;
    font-size: 12px;
    color: #94a3b8;
    cursor: pointer;
    border-radius: 2px;
    transition: color 0.2s ease, background-color 0.2s ease;

    &:hover {
      color: #ef4444;
      background: #fef2f2;
    }
  }
}

.picker-list {
  height: 320px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;

  &__item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    transition: background-color 0.2s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8fafc;
    }

    &.is-selected {
      background: #eff6ff;
    }

    &:focus-visible {
      outline: 2px solid #00a3ff;
      outline-offset: -2px;
    }
  }

  &__indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-right: 10px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #fff;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    i {
      font-size: 12px;
      font-weight: bold;
      color: #fff;
    }

    &.is-checked {
      background: #00a3ff;
      border-color: #00a3ff;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__account {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #1e293b;
    line-height: 1.4;
  }

  &__name {
    margin: 2px 0 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
  }

  &__footer {
    padding: 12px;
    text-align: center;
    font-size: 13px;
    color: #00a3ff;

    &--muted {
      color: #94a3b8;
    }
  }
}

.picker-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #94a3b8;

  i {
    font-size: 40px;
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }
}

.picker-footer {
  display: flex;
  justify-content: center;
  gap: 24px;

  &__cancel {
    color: #00a3ff;
    padding: 0;

    &:hover,
    &:focus {
      color: #33b5ff;
    }
  }

  &__confirm {
    min-width: 88px;
    background-color: #00a3ff;
    border-color: #00a3ff;
    border-radius: 4px;

    &:hover,
    &:focus {
      background-color: #33b5ff;
      border-color: #33b5ff;
    }
  }
}
</style>

<style lang="scss">
.user-picker-dialog {
  .el-dialog__body {
    padding: 16px 20px 8px;
  }

  .el-dialog__header {
    padding: 16px 20px 10px;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #1e293b;
    }
  }
}
</style>
